/**
 * Document-extraction schema registry — ADR-0083.
 *
 * Each entry defines, for one `AttachmentLabel`, the structured
 * field set the AI extractor should produce and what the mobile
 * review form should render. The registry is the single source of
 * truth: the provider builds Claude tool definitions from it, the
 * Zod validator at confirm time validates against it, and the
 * assistant snapshot serialises extracted records' fields keyed by
 * these names.
 *
 * Adding a new document label is a single registry entry — no new
 * TypeScript types, no new provider code, no new form components.
 * See ADR-0083 §Decision Outcome.
 *
 * Wire-contract invariants (enforced by `__tests__/document-schemas.test.ts`):
 *   - field `name`s are unique within a schema
 *   - `validityEndField` (when set) points at a real field of type
 *     `'date'`
 *   - `fewShotExample` keys are a subset of field `name`s
 *   - `schemaVersion` starts at 1; rename / drop / retype = bump
 *
 * Adding a new label after v1:
 *   1. Append the label to `AttachmentLabel` in `types.ts`.
 *   2. Add a `DocumentSchema` entry below.
 *   3. Add a passport-style few-shot example.
 *   4. Add an e2e spec asserting the schema-driven form renders the
 *      new field types correctly.
 *   That's the whole PR. The provider, router, redaction, snapshot,
 *   form, and predicate auto-pick up the new entry.
 */

import type {
  AttachmentLabel,
  DocumentSchema,
  ExtractedFieldValue,
} from './types';

const PASSPORT_SCHEMA: DocumentSchema = {
  label: 'passport',
  displayName: 'Passport',
  validityEndField: 'date_of_expiry',
  schemaVersion: 1,
  fields: [
    {
      name: 'document_number',
      label: 'Passport number',
      type: 'string',
      hint: 'The alphanumeric passport number printed on the data page.',
      important: true,
    },
    { name: 'surname', label: 'Surname', type: 'string' },
    { name: 'given_names', label: 'Given names', type: 'string' },
    {
      name: 'nationality',
      label: 'Nationality',
      type: 'country-code',
      hint: 'ISO 3166-1 alpha-2 country code (e.g. US, GB, JP).',
    },
    { name: 'date_of_birth', label: 'Date of birth', type: 'date' },
    {
      name: 'sex',
      label: 'Sex',
      type: 'enum',
      options: ['M', 'F', 'X'],
    },
    { name: 'date_of_issue', label: 'Date of issue', type: 'date' },
    {
      name: 'date_of_expiry',
      label: 'Date of expiry',
      type: 'date',
      important: true,
    },
    { name: 'place_of_issue', label: 'Place of issue', type: 'string' },
    {
      name: 'mrz',
      label: 'Machine-readable zone (MRZ)',
      type: 'string',
      hint: 'Both MRZ lines from the bottom of the data page, joined with a newline.',
    },
  ],
  fewShotExample: {
    document_number: 'X12345678',
    surname: 'CARTER',
    given_names: 'ALEX JORDAN',
    nationality: 'US',
    date_of_birth: '1992-04-12',
    sex: 'X',
    date_of_issue: '2021-06-30',
    date_of_expiry: '2031-06-29',
    place_of_issue: 'UNITED STATES OF AMERICA',
    mrz: 'P<USACARTER<<ALEX<JORDAN<<<<<<<<<<<<<<<<<<<<\nX123456784USA9204125M3106296<<<<<<<<<<<<<<<8',
  } satisfies Record<string, ExtractedFieldValue>,
};

const VISA_SCHEMA: DocumentSchema = {
  label: 'visa',
  displayName: 'Visa',
  validityEndField: 'valid_to',
  schemaVersion: 1,
  fields: [
    {
      name: 'country',
      label: 'Issuing country',
      type: 'country-code',
      hint: 'ISO 3166-1 alpha-2 of the country issuing the visa.',
      important: true,
    },
    {
      name: 'visa_type',
      label: 'Visa type',
      type: 'string',
      hint: "Free-form: 'tourist', 'business', 'Schengen', 'ESTA', etc.",
    },
    { name: 'valid_from', label: 'Valid from', type: 'date' },
    {
      name: 'valid_to',
      label: 'Valid to',
      type: 'date',
      important: true,
    },
    { name: 'max_stay_days', label: 'Maximum stay (days)', type: 'number' },
    {
      name: 'entries',
      label: 'Entries',
      type: 'enum',
      options: ['single', 'multiple'],
    },
    {
      name: 'reference_number',
      label: 'Visa / reference number',
      type: 'string',
    },
    { name: 'issued_to', label: 'Issued to (name on visa)', type: 'string' },
  ],
  fewShotExample: {
    country: 'JP',
    visa_type: 'Tourist',
    valid_from: '2026-09-01',
    valid_to: '2027-02-28',
    max_stay_days: 90,
    entries: 'multiple',
    reference_number: 'JV2026-887421',
    issued_to: 'ALEX JORDAN CARTER',
  } satisfies Record<string, ExtractedFieldValue>,
};

const INSURANCE_SCHEMA: DocumentSchema = {
  label: 'insurance',
  displayName: 'Travel insurance',
  validityEndField: 'coverage_end',
  schemaVersion: 1,
  fields: [
    { name: 'provider', label: 'Insurer', type: 'string', important: true },
    { name: 'policy_number', label: 'Policy number', type: 'string' },
    { name: 'policy_holder', label: 'Policy holder', type: 'string' },
    { name: 'coverage_start', label: 'Coverage starts', type: 'date' },
    {
      name: 'coverage_end',
      label: 'Coverage ends',
      type: 'date',
      important: true,
    },
    {
      name: 'max_medical_coverage',
      label: 'Max medical coverage',
      type: 'money',
    },
    {
      name: 'max_evacuation_coverage',
      label: 'Max evacuation coverage',
      type: 'money',
    },
    { name: 'excess_amount', label: 'Excess / deductible', type: 'money' },
    {
      name: 'coverage_countries',
      label: 'Coverage countries',
      type: 'multi-country',
      hint: "Comma-separated ISO alpha-2 codes, or 'worldwide' for global coverage.",
    },
    {
      name: 'emergency_contact_phone',
      label: 'Emergency phone',
      type: 'phone',
    },
    {
      name: 'emergency_contact_email',
      label: 'Emergency email',
      type: 'email',
    },
  ],
  fewShotExample: {
    provider: 'Allianz Global Assistance',
    policy_number: 'AGA-2026-554711',
    policy_holder: 'ALEX JORDAN CARTER',
    coverage_start: '2026-09-15',
    coverage_end: '2026-10-15',
    max_medical_coverage: { amount: 1_000_000, currency: 'USD' },
    max_evacuation_coverage: { amount: 500_000, currency: 'USD' },
    excess_amount: { amount: 100, currency: 'USD' },
    coverage_countries: 'worldwide',
    emergency_contact_phone: '+1-800-555-0143',
    emergency_contact_email: 'assist@allianz-travel.example',
  } satisfies Record<string, ExtractedFieldValue>,
};

const VACCINATION_SCHEMA: DocumentSchema = {
  label: 'vaccination',
  displayName: 'Vaccination',
  validityEndField: 'valid_until',
  // Blank `valid_until` means lifelong (MMR, polio, …) — opt in to
  // the snapshot resolver's "no expiry → covers any date" rule. The
  // other three labels (passport, visa, insurance) leave this
  // undefined: missing expiry there means extraction failed, not
  // that the document is valid forever.
  validityEndOptional: true,
  schemaVersion: 1,
  fields: [
    {
      name: 'vaccine',
      label: 'Vaccine',
      type: 'string',
      important: true,
    },
    { name: 'manufacturer', label: 'Manufacturer', type: 'string' },
    { name: 'batch_number', label: 'Batch number', type: 'string' },
    {
      name: 'date_administered',
      label: 'Date administered',
      type: 'date',
    },
    {
      name: 'valid_until',
      label: 'Valid until',
      type: 'date',
      hint: 'Leave blank for lifelong vaccinations (MMR, polio, etc.).',
    },
    {
      name: 'administered_by',
      label: 'Administered by',
      type: 'string',
      hint: 'Clinic, pharmacy, or vaccinator name.',
    },
    {
      name: 'certificate_number',
      label: 'Certificate number',
      type: 'string',
    },
    {
      name: 'certifying_authority',
      label: 'Certifying authority',
      type: 'string',
    },
  ],
  fewShotExample: {
    vaccine: 'Yellow fever',
    manufacturer: 'Sanofi Pasteur',
    batch_number: 'YF-44A831',
    date_administered: '2026-07-04',
    valid_until: '2036-07-04',
    administered_by: 'Travel Clinic San Diego',
    certificate_number: 'ICVP-USA-2026-119872',
    certifying_authority: 'WHO ICVP',
  } satisfies Record<string, ExtractedFieldValue>,
};

/** Registered schemas keyed by `AttachmentLabel`. Labels that aren't
 *  AI-extractable (e.g. `confirmation`, `ticket`, `boarding-pass` —
 *  ADR-0081 territory; `receipt`, `invoice`, `other` — out of scope
 *  for v1) return `undefined`. */
export const DOCUMENT_SCHEMAS: Readonly<
  Partial<Record<AttachmentLabel, DocumentSchema>>
> = {
  passport: PASSPORT_SCHEMA,
  visa: VISA_SCHEMA,
  insurance: INSURANCE_SCHEMA,
  vaccination: VACCINATION_SCHEMA,
};

/** Lookup helper — returns the schema or `undefined` for labels with
 *  no registered extraction (booking parser labels, generic labels). */
export function getDocumentSchema(
  label: AttachmentLabel | undefined,
): DocumentSchema | undefined {
  if (!label) return undefined;
  return DOCUMENT_SCHEMAS[label];
}

/** Convenience: the set of labels that have a registered schema. The
 *  mobile predicate uses this to decide whether to surface the
 *  "Extract details" action; the assistant snapshot iterates it when
 *  building the `extractedDocuments` map. */
export const EXTRACTABLE_LABELS: readonly AttachmentLabel[] = Object.keys(
  DOCUMENT_SCHEMAS,
) as AttachmentLabel[];
